import useStoreApp from '@/store';
import { BorderBottom } from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear';
import Box from '@mui/material/Box';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import React, {
  ReactElement,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { toast } from 'react-toastify';

const geocoderService = { current: null };
interface IProps {
  // id of input
  idText: string;
  // icon of input
  icon?: ReactElement;
  // set parent location's data
  setOutput: React.Dispatch<React.SetStateAction<any>>;
  // set value react-hook-form
  setValue?: (val: string, options?: any) => void;
  // register of react-hook-form
  register?: any;
  // init default location
  isInitDefaultLocation?: boolean;
  // set default location's data (current location or partner location)
  setDefaultLocation?: React.Dispatch<React.SetStateAction<string>>;
  // location address when pick and confirm from map dialog
  mapValue?: string | null;
  // check reset
  isReset?: boolean;
  tourPickUp?: string;
  tourPickUpAddress?: string;
  prefixIcon?: ReactElement;
  sxClearIcon?: any;
}

interface IPlace {
  lat?: number;
  lng?: number;
}

const LOCATION_NULL_DATA = `{"lat": null, "lng": null}`;

function InputAutocompleteClass(props: TextFieldProps & IProps) {
  const {
    idText,
    icon,
    setOutput,
    setValue,
    register,
    isInitDefaultLocation,
    setDefaultLocation,
    mapValue,
    isReset,
    tourPickUp,
    tourPickUpAddress,
    error,
    prefixIcon,
    sxClearIcon,
    ...otherProps
  } = props;
  const { onChange, onBlur, ...otherRegister } = register;

  // autocomplete class init and defined by id
  const autoComplete = { [idText]: null };

  const { user, isEmbeed } = useStoreApp(state => state.globalSlice);

  // text display on input
  const [inputValue, setInputValue] = useState<string>('');
  // location data which chosen by click an item of pac container
  const [place, setPlace] = useState<null | {
    location: string;
    address: string;
  }>(null);

  const [onClear, setOnClear] = useState<boolean>(false);

  const [isChanged, setIsChanged] = useState<boolean>(false);

  const [requestGeoLocationFailed, setRequestGeoLocationFailed] = useState<boolean>(false);

  const updateValueIsChange = useCallback(() => {
    if (!isChanged) {
      setIsChanged(true);
    }
  }, [isChanged]);

  // handle on change of input, set text display and handle on change of register
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {

    if (onClear) {
      setOnClear(false);
    }

    setInputValue(e.target.value);
    onChange(e);

    updateValueIsChange();
  }, [onClear, onChange, updateValueIsChange]);

  // handle on blur of input, set text display and handle on blur of register
  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // const pac = document.getElementById(`${idText}-pac-container`);
      const dontHavePlace = !place;
      if (dontHavePlace) {
        setInputValue('');
        // action to smooth when click input after it has value
        //if (pac?.childNodes && pac?.childNodes.length > 0) {
        setValue && setValue('', { shouldValidate: true });
        //}
      }
      onBlur(e);

      updateValueIsChange();
    }, [place]);

  // handle clear data, set text display, set form value and validate, set parent location's data
  const handleClear = useCallback(() => {
    setInputValue('');
    setValue && setValue('', { shouldValidate: true });
    setPlace(null);
    setOutput(null);

    if (!isReset && !onClear) {
      setOnClear(true);
    }

    updateValueIsChange();

  }, [onClear, setOutput, setValue, setPlace]);

  //  handle fetch detail of location by placeID or position <only for get current location>
  const fetchDetail = useCallback(
    (request: any) => {
      if (!geocoderService.current && (window as any).google) {
        geocoderService.current = new (window as any).google.maps.Geocoder();
      }

      if (!geocoderService.current) {
        return undefined;
      }

      (geocoderService.current as any).geocode(request, (results?: any) => {
        // set data location
        setPlace({
          location: JSON.stringify(results[0].geometry.location.toJSON()),
          address: results[0].formatted_address,
        });
        // set form value and validate
        setValue &&
          setValue(results[0].formatted_address, { shouldValidate: true });
        // set input text
        setInputValue(results[0].formatted_address);
        // set current location
        setDefaultLocation &&
          setDefaultLocation(
            JSON.stringify(results[0].geometry.location.toJSON()),
          );
        // set parent location's data
        setOutput({
          [idText]: JSON.stringify(results[0].geometry.location.toJSON()),
          [`${idText}Address`]: results[0].formatted_address,
          [`${idText}Ward`]: '',
          [`${idText}District`]: '',
          [`${idText}City`]: '',
        });
      });
    },
    [idText],
  );

  const isSameLocation = (location1: IPlace, location2: IPlace) => {
    return location1.lat === location2.lat && location1.lng === location2.lng;
  }

  useEffect(() => {

    if (Boolean(isReset)) {
      handleClear();
    }

    if (onClear) {
      return;
    }

    if (!requestGeoLocationFailed && isInitDefaultLocation && !isChanged) {

      // if user is logged in and user address is set, set location
      if (!isEmbeed && user && user.address && user.lat && user.lng) {

        const placeData = JSON.parse(Boolean(isReset) ? LOCATION_NULL_DATA : (place?.location ?? LOCATION_NULL_DATA)) as IPlace;

        if (isSameLocation(placeData, { lat: user.lat, lng: user.lng })) {
          return;
        }

        const location = {
          lat: user.lat,
          lng: user.lng,
        };

        setLocation(location, user.address);
        return;
      }
      // if browser does not support geolocation, return
      else if (!navigator.geolocation) {
        console.log('Geolocation API not supported by this browser.');
        return;
      }
      // if user is not logged in or user address is not set, get current location
      else {
        navigator.geolocation.getCurrentPosition(
          values => {
            const location = {
              lat: values?.coords?.latitude,
              lng: values?.coords?.longitude,
            };
            fetchDetail({ location });
          },
          (error) => {
            console.error('Không lấy được vị trí:', error.message);
            setRequestGeoLocationFailed(true);
          },
          { maximumAge: 0, timeout: 10000, enableHighAccuracy: true },
        );
      }
      if (place) {
        handleClear();
      }
    }
    else if (place && place.location === 'default') {
      console.log('have default location');
      return;
    }
    else if (tourPickUp && tourPickUpAddress) {

      const tourPickUpLocation = JSON.parse(tourPickUp);
      const placeData = JSON.parse(Boolean(isReset) ? LOCATION_NULL_DATA : (place?.location ?? LOCATION_NULL_DATA)) as IPlace;
      if (isSameLocation(placeData, tourPickUpLocation)) {
        return;
      }
      setLocation(tourPickUpLocation, tourPickUpAddress);
      return;
    }
  }, [idText, isInitDefaultLocation, user, isReset, tourPickUp, tourPickUpAddress, requestGeoLocationFailed]);


  const setLocation = (location: IPlace, address: string) => {
    setPlace({ location: JSON.stringify(location), address: address });
    setValue && setValue(address);
    setInputValue(address);
    setDefaultLocation && setDefaultLocation(JSON.stringify(location));

    setOutput({
      [idText]: JSON.stringify(location),
      [`${idText}Address`]: address,
      [`${idText}Ward`]: '',
      [`${idText}District`]: '',
      [`${idText}City`]: '',
    });
  }

  // init input autocomplete class
  useEffect(() => {
    try {
      if (autoComplete[idText] === null) {
        autoComplete[idText] = new (
          window as any
        ).google.maps.places.Autocomplete(document.getElementById(`${idText}`), {
          // 15.8817076,108.3301273
          bounds: {
            east: 108.3301273 + 0.1,
            north: 15.8817076 + 0.1,
            south: 15.8817076 - 0.1,
            west: 108.3301273 - 0.1,
          },
          componentRestrictions: {
            country: 'vn',
          },
          fields: [
            'address_components',
            'place_id',
            'geometry',
            'formatted_address',
            'name',
          ],
          strictBounds: true,
        });
      }
      // handle event select address
      (autoComplete[idText] as any).addListener('place_changed', () => {

        const selected = (autoComplete[idText] as any).getPlace();

        // handle for click done or enter on mobile when not select
        if (!selected.geometry || !selected.geometry.location) {
          setInputValue('');
          setValue && setValue('', { shouldValidate: true });
          return;
        }
        // handle data, input text, input form value and validate
        setPlace({
          location: JSON.stringify(selected.geometry.location.toJSON()),
          address: selected.name,
        });
        setValue && setValue(selected.name, { shouldValidate: true });
        setInputValue(selected.name);
        setOutput({
          [idText]: JSON.stringify(selected.geometry.location.toJSON()),
          [`${idText}Address`]: selected.name,
          [`${idText}Ward`]: '',
          [`${idText}District`]: '',
          [`${idText}City`]: '',
        });
      });
    } catch (error) {
      console.error('InputAutocompleteClass -> Error in useEffect', error);
    }
  }, [idText]);

  // handle when change text after having place but not select address
  useEffect(() => {
    if (!!place && inputValue !== place.address) {
      setPlace(null);
      setOutput(null);
    }
  }, [inputValue, place]);


  // set place data, set input text, input form value and validate
  useEffect(() => {
    if (
      typeof mapValue === 'string' &&
      (mapValue !== inputValue || !inputValue)
    ) {
      setPlace({ location: 'default', address: mapValue });
      setValue && setValue(mapValue, { shouldValidate: true });
      setInputValue(mapValue);
    }
  }, [mapValue]);

  return (
    <StyleWrap hasError={Boolean(error)}>

      {prefixIcon ?
        (
          <Box sx={{ mx: 1 }}>
            {prefixIcon}
          </Box>
        ) :
        null
      }
      <StyledInputComp
        id={idText}
        onChange={handleChange}
        onBlur={handleBlur}
        value={inputValue}
        error={error}
        {...otherRegister}
        {...otherProps}
      />
      {icon ? icon : null}
      <ClearIcon
        className='clear-icon'
        fontSize="small"
        onClick={handleClear}
        sx={{ visibility: inputValue ? 'visible' : 'hidden', ...sxClearIcon }}
      />
    </StyleWrap>
  );
}

export default memo(InputAutocompleteClass);

const StyleWrap = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasError',
})<{ hasError?: boolean }>(({ theme, hasError }) => ({
  width: '100%',
  position: 'relative',

  display: 'flex',
  alignItems: 'center',

  gap: '0.25rem',

  '.clear-icon': {
    position: 'absolute',
    right: 50,
    top: '50%',
    transform: 'translateY(-50%)',
    color: theme.palette.black[20],
    cursor: 'pointer',
  },

  borderBottom: '1px solid',
  borderColor: hasError ? theme.palette.error.main : '#c4c4c4',

  '&:hover': {
    '&:before': {
      borderBottom: 'unset!important',
    },
  },
}));

const StyledInputComp = styled(TextField)<TextFieldProps>(({ theme }) => ({
  height: 'auto',
  width: '100%',

  label: {
    lineHeight: 'unset',
    top: '-2px',
    left: '-0.75rem',
  },

  p: {
    marginLeft: '0 !important',
  },

  // css for TextField
  '.MuiOutlinedInput-root': {
    height: '48px',
    fontSize: '14px',
    lineHeight: '16px',

    input: {
      height: '100%',
      padding: '0px 36px 0px 0px !important',
      fontWeight: 600,
    },

    borderBottom: 'none',

    fieldset: {
      borderRadius: '8px',
    },
  },
  '& .MuiFilledInput-root': {
    height: 48,
    fontWeight: 600,
    overflow: 'hidden',
    backgroundColor: 'transparent',
    boxShadow: 'unset',
    transition: theme.transitions.create([
      'border-color',
      'background-color',
      'box-shadow',
    ]),
    input: {
      height: '100%',
      paddingRight: '32px',
      paddingLeft: '0px !important',
      fontWeight: 600,
      fontSize: 14,
      color: theme.palette.black.main,
      textOverflow: 'ellipsis',
    },
    '&:before': {
      borderBottom: 'unset',
    },
    '&:after': {
      borderBottom: 'unset',
    },
    '&:hover': {
      //backgroundColor: 'transparent',

      '&:before': {
        borderBottom: 'unset!important',
      },
    },
    '&.Mui-focused': {
      backgroundColor: 'transparent',
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-readOnly': {
      '&:hover': {
        border: `1px solid #E0E3E7`,
        backgroundColor: theme.palette.white.main,
        boxShadow: 'unset',
        '&::before': {
          border: 'unset',
        },
      },
      '&:before': {
        borderBottom: 'unset',
      },
    },
    '&.Mui-error': {
      borderBottomColor: theme.palette.error.main,
    },
  },

  [theme.breakpoints.down('sm')]: {
    '.MuiOutlinedInput-root': {
      fontSize: 'unset',
      lineHeight: 'unset',
    },
  },
}));
